import * as api from '../../../../api';

import { Formik, FormikProps } from 'formik';
import { useState } from 'react';
import { Modal, VStack, Button } from '@vivaahealth/design-system';
import { useTranslation } from 'react-i18next';

import EmergencyContactConfirmationDialog from './EmergencyContactConfirmationDialog';
import EmergencyContactButton from '../EmergencyContactButton';

import {
  maybeSaveAsNew,
  buildEmergencyContact,
  generateTranslatedErrorMessage,
} from '../utils';
import {
  EditEmergencyContactSchema,
  EditEmergencyContactValues,
} from './schema';
import { EmergencyContactFields } from './EmergencyContactFields';

interface EmergencyContactModalProps {
  topLabel: string;
  rank: number;
  isOpen: boolean;
  emergencyContacts: api.Previsit.PatientEmergencyContact[];
  emergencyContactRelationships: api.Patient.EmergencyContactRelationship[];
  isEditExperience?: boolean;
  onModalClose: () => void;
  saveFieldValue: (value: string) => void;
  updateFieldValue: (value: string) => void;
  startsAtOne: boolean;
}

const EditEmergencyContactModal = (props: EmergencyContactModalProps) => {
  const {
    topLabel,
    rank,
    onModalClose,
    isOpen,
    saveFieldValue,
    emergencyContacts,
    updateFieldValue,
    emergencyContactRelationships,
    startsAtOne,
  } = props;

  const { t } = useTranslation('emergencyContacts');

  const adjustedRank = startsAtOne ? rank - 1 : rank;
  const emergencyContact = emergencyContacts[adjustedRank] || {};

  const [dialogIsOpen, setDialogIsOpen] = useState(false);
  const [removeDialogIsOpen, setRemoveDialogIsOpen] = useState(false);

  const initialValues: EditEmergencyContactValues = {
    firstName: emergencyContact.firstName,
    lastName: emergencyContact.lastName,
    cellPhoneNumber: emergencyContact.cellPhoneNumber,
    homePhoneNumber: emergencyContact.homePhoneNumber,
    rank: emergencyContact.rank,
    emergencyContactRelationshipToPatient:
      emergencyContact.emergencyContactRelationshipToPatient,
    patientRelationshipToEmergencyContact:
      emergencyContact.patientRelationshipToEmergencyContact || null,
    status:
      emergencyContact.status === api.Previsit.ModificationStatus.Added
        ? api.Previsit.ModificationStatus.Added
        : api.Previsit.ModificationStatus.Updated,
    isMobile: !!emergencyContact.cellPhoneNumber,
    isHome:
      !!emergencyContact.homePhoneNumber && !emergencyContact.cellPhoneNumber,
  };

  const calculateNumberOfUpdates = (values: EditEmergencyContactValues) => {
    const updates = maybeSaveAsNew({
      newContact: buildEmergencyContact(values),
      previousContact: emergencyContact,
    });
    return updates;
  };

  const handleSubmit = (values: EditEmergencyContactValues) => {
    const updatedContact = buildEmergencyContact(values);

    emergencyContacts[
      startsAtOne ? updatedContact.rank - 1 : updatedContact.rank
    ] = updatedContact;

    saveFieldValue(JSON.stringify(emergencyContacts));
    updateFieldValue(JSON.stringify(emergencyContacts));
    onModalClose();
  };

  const generateErrorMessages = generateTranslatedErrorMessage(t);

  return (
    <Modal
      open={isOpen}
      size="large"
      title={topLabel}
      isFullScreenOnMobile
      onClose={() => onModalClose()}
    >
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={EditEmergencyContactSchema(generateErrorMessages)}
      >
        {(formikProps: FormikProps<EditEmergencyContactValues>) => {
          const isFormValid = formikProps.isValid && formikProps.dirty;
          return (
            <>
              {dialogIsOpen && (
                <EmergencyContactConfirmationDialog
                  dialog={{
                    isOpen: dialogIsOpen,
                    onClose: () => setDialogIsOpen(false),
                    title: t('newEmergencyContactTitle'),
                    description: t('emergencyContactUpdateContent'),
                  }}
                  emergencyContact={{
                    newRank: startsAtOne
                      ? emergencyContacts.length + 1
                      : emergencyContacts.length,
                  }}
                  formikProps={formikProps}
                />
              )}
              {removeDialogIsOpen && (
                <EmergencyContactConfirmationDialog
                  dialog={{
                    title: t('removeItem', {
                      ns: 'common',
                      item: `${formikProps.values.firstName} ${formikProps.values.lastName}`,
                    }),
                    description: t('removeEmergencyContactConfirmation'),
                    isOpen: removeDialogIsOpen,
                    onClose: () => setRemoveDialogIsOpen(false),
                    isRemoveExperience: true,
                  }}
                  emergencyContact={{
                    newRank: rank,
                  }}
                  formikProps={formikProps}
                />
              )}
              <VStack gap={20}>
                <EmergencyContactFields
                  formikProps={formikProps}
                  emergencyContactRelationships={emergencyContactRelationships}
                />
                <VStack alignItems={'center'} gap={16}>
                  {formikProps.dirty && (
                    <Button
                      variant="primary"
                      fullWidth
                      onClick={() => {
                        if (
                          calculateNumberOfUpdates(formikProps.values) >= 2 &&
                          isFormValid
                        ) {
                          setDialogIsOpen(true);
                        } else {
                          formikProps.submitForm();
                        }
                      }}
                    >
                      {t('saveUpdates', { ns: 'common' })}
                    </Button>
                  )}
                  <EmergencyContactButton
                    message={t('removeEmergencyContact')}
                    onClickHandler={() => setRemoveDialogIsOpen(true)}
                    colorVariant="danger"
                  />
                </VStack>
              </VStack>
            </>
          );
        }}
      </Formik>
    </Modal>
  );
};

export default EditEmergencyContactModal;
